'use client';
import type { ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { Button } from '@/app/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import { cn } from '@/app/utils';

export interface DialogOptions {
  /** Dialog标题 */
  title?: string;
  /** Dialog描述 */
  description?: string;
  /** Dialog内容 */
  content: ReactNode;
  /** 底部按钮区域 */
  footer?: ReactNode;
  /** 是否显示关闭按钮 */
  closable?: boolean;
  /** Dialog样式类名 */
  className?: string;
  /** 覆盖层样式类名 */
  overlayClassName?: string;
  /** Dialog变体 */
  variant?: 'default' | 'fullScreen' | 'mobileFullScreen';
  /** 关闭时的回调 */
  onClose?: () => void;
  /** 打开时的回调 */
  onOpen?: () => void;
}

interface DialogInstance {
  close: () => void;
  update: (options: Partial<DialogOptions>) => void;
}

/**
 * 打开一个Dialog
 * @param options Dialog配置选项
 * @returns Dialog实例，包含close和update方法
 */
export const openDialog = (options: DialogOptions): DialogInstance => {
  // 创建容器元素
  const container = document.createElement('div');
  document.body.appendChild(container);

  // 创建React根节点
  const root = createRoot(container);

  let currentOptions = { ...options };
  let isOpen = true;

  const close = () => {
    if (!isOpen) return;

    isOpen = false;
    currentOptions.onClose?.();

    // 延迟销毁，等待动画完成
    setTimeout(() => {
      root.unmount();
      if (container.parentNode) {
        container.parentNode.removeChild(container);
      }
    }, 200);

    render();
  };

  const update = (newOptions: Partial<DialogOptions>) => {
    if (!isOpen) return;

    currentOptions = { ...currentOptions, ...newOptions };
    render();
  };

  const render = () => {
    const DialogComponent = () => (
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            close();
          }
        }}
      >
        <DialogContent
          className={currentOptions.className}
          overlayClassname={currentOptions.overlayClassName}
          closable={currentOptions.closable ?? true}
          variant={currentOptions.variant}
          onClose={close}
        >
          {(currentOptions.title || currentOptions.description) && (
            <DialogHeader>
              {currentOptions.title && <DialogTitle>{currentOptions.title}</DialogTitle>}
              {currentOptions.description && <DialogDescription>{currentOptions.description}</DialogDescription>}
            </DialogHeader>
          )}

          {currentOptions.content}

          {currentOptions.footer && <DialogFooter>{currentOptions.footer}</DialogFooter>}
        </DialogContent>
      </Dialog>
    );

    root.render(<DialogComponent />);
  };

  // 初始渲染
  render();

  // 调用打开回调
  currentOptions.onOpen?.();

  return {
    close,
    update,
  };
};

/**
 * 快捷方法：打开提示Dialog
 */
export const openAlertDialog = (options: {
  title?: string;
  description?: string;
  content?: ReactNode;
  buttonText?: string;
  onClose?: () => void;
  className?: string;
  closable?: boolean;
}) => {
  const { title, description, content, buttonText, onClose, className, closable } = options;

  const dialogInstance = openDialog({
    title,
    description,
    content: content,
    className: cn(className, 'z-[221]'),
    overlayClassName: 'z-[220]',
    closable,
    footer: (
      <div className="flex justify-end">
        <Button
          variant="primary"
          onClick={() => {
            onClose?.();
            dialogInstance.close();
          }}
        >
          {buttonText}
        </Button>
      </div>
    ),
  });
};
